import { memo, useMemo } from "react";
import { useRecoilValue } from "recoil";
import { NavLink } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import { useQuery, UseQueryResult } from "react-query";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Box,
  Chip,
  SvgIconTypeMap,
  Theme,
  Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BuildIcon from "@mui/icons-material/Build";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import TuneIcon from "@mui/icons-material/Tune";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import ChecklistRtlIcon from "@mui/icons-material/ChecklistRtl";
import CalculateIcon from "@mui/icons-material/Calculate";
import HandymanIcon from "@mui/icons-material/Handyman";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { useIntl } from "react-intl";

import { WorkflowUpdateErrorCount } from "../../services/Price";
import { Module } from "../../services/Module";
import { RevbellMenuItem } from "./Menu";
import { Nancie, RevbellIcon } from "../../components/icons";
import { InternalRedirection, SubMenuName } from "../interface";
import {
  extractRoutes,
  formatConfigurationJson,
  getConfigurationPagesFromAdminMenus,
  getFirstAdminMenuPage,
  isRouteAccessible,
  withHardcodedRoutes,
} from "./utils";
import { listWorkflowUpdatesError } from "../../services/WorkflowUpdates";
import { demoModeState, useDemoMode } from "../../services/demoMode";
import {
  currentUserAtom,
  menuItemsAtom,
  Roles,
  selectedCustomerAtom,
  useHasRole,
} from "../../AuthProvider";
import {
  ADMIN_REVBELL_SUBMENU_HIERARCHY,
  ADMIN_SUBMENU_HIERARCHY,
} from "./menusStructure";
import CustomerDropdown from "./CustomerDropdown";
import { favoritesAtom } from "../atom";
import FavoriteButton from "./FavoriteButton";
import StarIcon from "@mui/icons-material/Star";

export const baseStylesMenuItem = (theme: Theme) => ({
  color: theme.vars.palette.menu.text,
  display: "block",
  height: "36px",

  "@media only screen and (max-height: 768px)": {
    "&.MuiListItem-root": {
      height: "32px",

      "& svg": {
        height: "22px",
      },
    },
  },

  "&:hover": {
    backgroundColor: theme.vars.palette.menu.hover,
  },

  "& svg": {
    fill: theme.vars.palette.menu.text,
  },

  "&.active": {
    color: theme.vars.palette.text.accentInverted,

    "& svg": {
      fill: theme.vars.palette.text.accentInverted,
    },
  },
});

const useStyles = makeStyles()((theme: Theme) => ({
  chip: {
    "& .MuiBadge-badge": {
      backgroundColor: theme.vars.palette.error.main,
      color: theme.vars.palette.common.white,
    },
  },
  newChip: {
    height: "14px",
    fontSize: "0.5rem",
    fontWeight: 700,
    textTransform: "uppercase",
    // Sits as a superscript on the label rather than beside it, so it reads as
    // a marker on the entry and not as a second word in the menu.
    alignSelf: "flex-start",
    marginTop: "8px",
    "& .MuiChip-label": { paddingLeft: "4px", paddingRight: "4px" },
  },
  menuItem: { ...baseStylesMenuItem(theme) },
  menuItemButton: {
    height: "100%",
  },
  menuItemRevbell: {
    ...baseStylesMenuItem(theme),

    "&:hover": {
      backgroundColor: "unset",
    },
  },
  menuItemButtonRevbell: {
    height: "100%",
    cursor: "unset",
    display: "flex",
    alignItems: "center",

    "&:hover": {
      backgroundColor: "unset",
    },

    "& .MuiListItemIcon-root svg": {
      height: "30px",
    },
  },
}));

export enum MenuSlug {
  DASHBOARD = "dashboard",
  PILOTAGE_OPTIMISATION = "pilotage_optimisation",
  REPORTING = "cockpit",
  EVENT = "events",
  ALERTS = "active_alerts",
  CALENDAR = "mass_pricing",
  GROUP_QUOTATION = "group_quotation",
  WORKFLOW = "pricing",
  CONFIGURATION = "react_configuration",
  FILE_TRANSFERT = "transfert",
}

type MenuKeyFields = {
  [key in MenuSlug]: OverridableComponent<SvgIconTypeMap<{}, "svg">> & {
    muiName: string;
  };
};

const MenuIcons: MenuKeyFields = {
  [MenuSlug.DASHBOARD]: DashboardCustomizeIcon,
  [MenuSlug.PILOTAGE_OPTIMISATION]: TuneIcon,
  [MenuSlug.REPORTING]: QueryStatsIcon,
  [MenuSlug.EVENT]: CalendarViewWeekIcon,
  [MenuSlug.ALERTS]: NotificationsIcon,
  [MenuSlug.CALENDAR]: CalendarTodayIcon,
  [MenuSlug.GROUP_QUOTATION]: CalculateIcon,
  [MenuSlug.WORKFLOW]: ChecklistRtlIcon,
  [MenuSlug.CONFIGURATION]: BuildIcon,
  [MenuSlug.FILE_TRANSFERT]: FileUploadIcon,
};

const isActiveMenuPilotageOptimisation = (): boolean => {
  return [
    "/alerte/hebergements/",
    "/cockpit/",
    "/calendar/",
    "/price/",
    "/configuration/setting/autopilot",
    "/configuration/setting/business-rules",
    "/configuration/setting/events",
    "/configuration/setting/model",
    "/configuration/setting/models",
    "/configuration/setting/seasons",
  ].some((route) => window.location.href.includes(route));
};

const isActiveMenuAdminRevbell = (): boolean => {
  return ["/banner/", "/customers/"].some((route) =>
    window.location.href.includes(route)
  );
};

const isActiveMenuAdmin = (): boolean => {
  const flatAdminMenus = ADMIN_SUBMENU_HIERARCHY.flatMap((menu) =>
    menu.nestedOptions ? (menu.nestedOptions as InternalRedirection[]) : []
  );

  return flatAdminMenus.some(
    (menu) =>
      menu.redirectionTo && window.location.href.includes(menu.redirectionTo)
  );
};

const isActiveMenu = (menuItem: RevbellMenuItem): boolean => {
  const confPageFromAdminMenu = getConfigurationPagesFromAdminMenus();

  if (menuItem.isReactPage) {
    if (
      confPageFromAdminMenu.some((menu) =>
        menu.redirectionTo
          ? window.location.href.indexOf(menu.redirectionTo) !== -1
          : false
      )
    ) {
      return false;
    } else if (
      window.location.href.indexOf(menuItem.reactRoute) !== -1 ||
      window.location.href.indexOf(menuItem.reactRoute.slice(0, -1)) !== -1
    ) {
      return true;
    }
  }

  return window.location.pathname === "/" && menuItem.slug === Module.DASHBOARD;
};

// Modules recent enough that the menu points them out. One line to add, one to
// remove once they stop being news.
const NEW_MODULES: string[] = [MenuSlug.GROUP_QUOTATION];

const getBadgetAmount = (
  menuItem: RevbellMenuItem,
  unmappedAmount: number | null,
  queryWorkflowUpdateError: UseQueryResult<WorkflowUpdateErrorCount, unknown>
) => {
  if (menuItem.slug === Module.CONFIGURATION && unmappedAmount) {
    return unmappedAmount > 500 ? "500+" : unmappedAmount;
  } else if (
    menuItem.slug === Module.PRICING &&
    queryWorkflowUpdateError.data &&
    queryWorkflowUpdateError.data.error_count > 0
  ) {
    return queryWorkflowUpdateError.data.error_count;
  }

  return null;
};

const GenericMenuItems = ({
  menuItem,
  unmappedAmount,
  queryWorkflowUpdateError,
  open,
  onSubMenuOpen,
  onSubMenuClose,
}: {
  menuItem: RevbellMenuItem;
  unmappedAmount: number | null;
  queryWorkflowUpdateError: UseQueryResult<WorkflowUpdateErrorCount, unknown>;
  open: boolean;
  onSubMenuOpen: (item: string, translatedMenu?: string) => void;
  onSubMenuClose: (
    e: React.MouseEvent,
    drawer: string,
    isItem?: boolean
  ) => void;
}) => {
  const { classes } = useStyles();
  const favorites = useRecoilValue(favoritesAtom);
  const itemId = menuItem.reactRoute || menuItem.urlPath;
  const isFavorite = favorites.some((f) => f.id === itemId);

  const MenuIcon = MenuIcons[menuItem.slug as MenuSlug] || HandymanIcon;
  const isActive =
    menuItem.slug === MenuSlug.PILOTAGE_OPTIMISATION
      ? isActiveMenuPilotageOptimisation()
      : isActiveMenu(menuItem);
  const badgeAmount = getBadgetAmount(
    menuItem,
    unmappedAmount,
    queryWorkflowUpdateError
  );

  return (
    <ListItem
      key={menuItem.id}
      disablePadding
      className={`${classes.menuItem} ${isActive ? "active" : ""}`}
      sx={{
        "& .fav-hidden": { visibility: "hidden" },
        "&:hover .fav-hidden": { visibility: "visible" },
      }}
      data-has-submenu={
        menuItem.slug === MenuSlug.CONFIGURATION ||
        menuItem.slug === MenuSlug.PILOTAGE_OPTIMISATION
      }
      onMouseOver={() => {
        if (menuItem.slug === MenuSlug.CONFIGURATION)
          onSubMenuOpen(SubMenuName.CONFIGURATION, menuItem.translated_name);
        if (menuItem.slug === MenuSlug.REPORTING)
          onSubMenuOpen(SubMenuName.REPORTING, menuItem.translated_name);
        if (menuItem.slug === MenuSlug.PILOTAGE_OPTIMISATION)
          onSubMenuOpen(SubMenuName.PILOTAGE_OPTIMISATION);
      }}
      onMouseLeave={(e) => onSubMenuClose(e, ".sub-drawer", true)}
    >
      <ListItemButton
        className={classes.menuItemButton}
        component={NavLink}
        to={menuItem.isReactPage ? menuItem.reactRoute : menuItem.urlPath}
        sx={[
          { py: 0, pr: 1, pl: 2.25 },
          open ? { justifyContent: "initial" } : { justifyContent: "center" },
        ]}
      >
        <Badge
          badgeContent={badgeAmount}
          className={classes.chip}
          sx={open ? { mr: 3 } : { mr: "auto" }}
        >
          <ListItemIcon
            sx={[
              {
                minWidth: 0,
                justifyContent: "center",
              },
            ]}
          >
            <MenuIcon />
          </ListItemIcon>
        </Badge>
        <ListItemText
          primary={menuItem.translated_name}
          sx={[open ? { opacity: 1 } : { opacity: 0 }]}
        />
        {open && NEW_MODULES.includes(menuItem.slug) && (
          <Chip
            size="small"
            color="primary"
            label={"New"}
            className={classes.newChip}
          />
        )}
        {open &&
          menuItem.slug !== MenuSlug.CONFIGURATION &&
          menuItem.slug !== MenuSlug.PILOTAGE_OPTIMISATION &&
          menuItem.slug !== MenuSlug.REPORTING && (
            <Box className={isFavorite ? undefined : "fav-hidden"}>
              <FavoriteButton id={itemId} label={menuItem.translated_name} />
            </Box>
          )}
      </ListItemButton>
    </ListItem>
  );
};

const MenuItems = memo(
  ({
    open,
    menuItems,
    onSubMenuOpen,
    onSubMenuClose,
    adminMenus,
    unmappedAmount,
  }: {
    open: boolean;
    menuItems: RevbellMenuItem[];
    onSubMenuOpen: (item: string, translatedMenu?: string) => void;
    onSubMenuClose: (
      e: React.MouseEvent,
      drawer: string,
      isItem?: boolean
    ) => void;
    adminMenus: InternalRedirection[];
    unmappedAmount: number | null;
  }) => {
    const currentUser = useRecoilValue(currentUserAtom);
    const selectedCustomer = useRecoilValue(selectedCustomerAtom);
    const { hasRole } = useHasRole();
    const demoMode = useRecoilValue(demoModeState);
    const favorites = useRecoilValue(favoritesAtom);
    const allMenuItems = useRecoilValue(menuItemsAtom);
    const { classes } = useStyles();
    const intl = useIntl();
    const { toggleDemo } = useDemoMode();

    const isStaf = useMemo(() => hasRole(Roles.SuperAdmin), [hasRole]);

    const visibleFavoritesCount = useMemo(() => {
      if (allMenuItems.length === 0) return favorites.length;
      const accessibleRoutes = withHardcodedRoutes(
        new Set([
          ...allMenuItems.flatMap((m) =>
            [m.reactRoute, m.urlPath].filter((r): r is string => !!r)
          ),
          ...extractRoutes(
            formatConfigurationJson(currentUser, selectedCustomer)
          ),
          ...(isStaf ? extractRoutes(ADMIN_REVBELL_SUBMENU_HIERARCHY) : []),
        ])
      );
      return favorites.filter((fav) =>
        isRouteAccessible(fav.id, accessibleRoutes)
      ).length;
    }, [favorites, allMenuItems, currentUser, isStaf, selectedCustomer]);

    const firstAdminUrl = useMemo(
      () =>
        adminMenus.length > 0 ? getFirstAdminMenuPage(adminMenus, isStaf) : "",
      [adminMenus, isStaf]
    );

    const queryWorkflowUpdateError: UseQueryResult<
      WorkflowUpdateErrorCount,
      unknown
    > = useQuery("price-error", () => listWorkflowUpdatesError());

    return (
      <>
        <List>
          <ListItem
            disablePadding
            sx={{
              mb: 2.25,
            }}
            className={classes.menuItemRevbell}
          >
            <Box
              className={classes.menuItemButtonRevbell}
              sx={[
                { py: 0, px: 1.875 },
                open
                  ? { justifyContent: "initial" }
                  : { justifyContent: "center" },
              ]}
            >
              <ListItemIcon
                sx={[
                  {
                    minWidth: 0,
                    justifyContent: "center",
                    position: "relative",
                    cursor: currentUser?.is_staff ? "pointer" : "unset",
                  },
                  open ? { mr: 3 } : { mr: "auto" },
                ]}
                onClick={() => {
                  if (currentUser?.is_staff) {
                    toggleDemo();
                  }
                }}
              >
                <RevbellIcon />
                {demoMode && (
                  <Typography
                    sx={(theme) => ({
                      position: "absolute",
                      fontSize: "0.675rem",
                      top: "28px",
                      px: 0.25,
                      background: theme.vars.palette.nancie.gradient,
                      borderRadius: "5px",
                      color: theme.vars.palette.common.white,
                    })}
                  >
                    DEMO
                  </Typography>
                )}
              </ListItemIcon>
            </Box>
          </ListItem>

          <CustomerDropdown open={open} />

          {visibleFavoritesCount > 0 && (
            <ListItem
              disablePadding
              data-has-submenu={true}
              onMouseOver={() => onSubMenuOpen(SubMenuName.FAVORITES)}
              onMouseLeave={(e) => onSubMenuClose(e, ".sub-drawer", true)}
              onFocus={() => onSubMenuOpen(SubMenuName.FAVORITES)}
              className={classes.menuItem}
            >
              <ListItemButton
                className={classes.menuItemButton}
                onClick={() => onSubMenuOpen(SubMenuName.FAVORITES)}
                sx={[
                  { py: 0, pr: 1, pl: 2.25 },
                  open
                    ? { justifyContent: "initial" }
                    : { justifyContent: "center" },
                ]}
              >
                <ListItemIcon
                  sx={[
                    { minWidth: 0, justifyContent: "center" },
                    open ? { mr: 3 } : { mr: "auto" },
                  ]}
                >
                  <StarIcon />
                </ListItemIcon>
                <ListItemText
                  primary={intl.formatMessage({ id: "FAVORITES_MENU" })}
                  sx={[open ? { opacity: 1 } : { opacity: 0 }]}
                />
              </ListItemButton>
            </ListItem>
          )}

          <ListItem
            disablePadding
            onMouseOver={(e) => onSubMenuClose(e, ".sub-drawer", true)}
            className={`${classes.menuItem} ${
              window.location.href.includes("/data-assistant/") ? "active" : ""
            }`}
          >
            <ListItemButton
              className={classes.menuItemButton}
              component={NavLink}
              to="/data-assistant/"
              sx={[
                { py: 0, pr: 1, pl: 2.25 },
                open
                  ? { justifyContent: "initial" }
                  : { justifyContent: "center" },
              ]}
            >
              <ListItemIcon
                sx={[
                  {
                    minWidth: 0,
                    justifyContent: "center",
                  },
                  open ? { mr: 3 } : { mr: "auto" },
                ]}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    "& svg": { width: "19px", height: "19px" },
                  }}
                >
                  <Nancie />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={intl.formatMessage({ id: "IA_NANCIE_MENU" })}
                sx={[open ? { opacity: 1 } : { opacity: 0 }]}
              />
            </ListItemButton>
          </ListItem>

          {menuItems.map((menuItem: RevbellMenuItem) => (
            <GenericMenuItems
              key={menuItem.slug}
              menuItem={menuItem}
              unmappedAmount={unmappedAmount}
              queryWorkflowUpdateError={queryWorkflowUpdateError}
              open={open}
              onSubMenuOpen={onSubMenuOpen}
              onSubMenuClose={onSubMenuClose}
            />
          ))}

          {adminMenus.length > 0 && (
            <ListItem
              disablePadding
              data-has-submenu={true}
              onMouseOver={(event) => {
                onSubMenuOpen(SubMenuName.ADMINISTRATION_SETTINGS);
              }}
              onMouseLeave={(e) => onSubMenuClose(e, ".sub-drawer", true)}
              className={`${classes.menuItem} ${
                isActiveMenuAdmin() ? "active" : ""
              }`}
            >
              <ListItemButton
                className={classes.menuItemButton}
                component={NavLink}
                to={firstAdminUrl || ""}
                sx={[
                  { py: 0, pr: 1, pl: 2.25 },
                  open
                    ? { justifyContent: "initial" }
                    : { justifyContent: "center" },
                ]}
              >
                <ListItemIcon
                  sx={[
                    {
                      minWidth: 0,
                      justifyContent: "center",
                    },
                    open ? { mr: 3 } : { mr: "auto" },
                  ]}
                >
                  <AdminPanelSettingsIcon />
                </ListItemIcon>
                <ListItemText
                  primary={intl.formatMessage({ id: "ADMINISTRATION_MENU" })}
                  sx={[open ? { opacity: 1 } : { opacity: 0 }]}
                />
              </ListItemButton>
            </ListItem>
          )}

          {isStaf && (
            <ListItem
              disablePadding
              data-has-submenu={true}
              onMouseOver={() => {
                onSubMenuOpen(SubMenuName.ADMIN_REVBELL);
              }}
              onMouseLeave={(e) => onSubMenuClose(e, ".sub-drawer", true)}
              className={`${classes.menuItem} ${
                isActiveMenuAdminRevbell() ? "active" : ""
              }`}
            >
              <ListItemButton
                className={classes.menuItemButton}
                component={NavLink}
                to="/banner/"
                sx={[
                  { py: 0, pr: 1, pl: 2.25 },
                  open
                    ? { justifyContent: "initial" }
                    : { justifyContent: "center" },
                ]}
              >
                <ListItemIcon
                  sx={[
                    {
                      minWidth: 0,
                      justifyContent: "center",
                    },
                    open ? { mr: 3 } : { mr: "auto" },
                  ]}
                >
                  <ManageAccountsIcon />
                </ListItemIcon>
                <ListItemText
                  primary={intl.formatMessage({ id: "ADMIN_REVBELL_MENU" })}
                  sx={[open ? { opacity: 1 } : { opacity: 0 }]}
                />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </>
    );
  }
);

export default MenuItems;
